import { Fragment, useEffect, useState } from 'react';
import { auditLogAPI } from '../../services/api';

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  invite: 'bg-violet-100 text-violet-700',
  invite_all: 'bg-violet-100 text-violet-700',
  admit: 'bg-blue-100 text-blue-700',
  life_member_promote: 'bg-fuchsia-100 text-fuchsia-700',
};

function actionBadge(action) {
  const cls = ACTION_COLORS[action] || 'bg-slate-100 text-slate-600';
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${cls}`}>{action}</span>;
}

function ValueDiff({ oldValues, newValues }) {
  if (!oldValues && !newValues) return <p className="text-xs text-slate-400">No details recorded.</p>;

  if (!oldValues) {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 mb-1.5">New Values</p>
        <pre className="text-xs text-emerald-800 whitespace-pre-wrap break-all font-mono">{JSON.stringify(newValues, null, 2)}</pre>
      </div>
    );
  }

  const keys = Array.from(new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]))
    .filter((k) => newValues && Object.prototype.hasOwnProperty.call(newValues, k))
    .filter((k) => JSON.stringify(oldValues?.[k]) !== JSON.stringify(newValues?.[k]));

  if (keys.length === 0) {
    return <p className="text-xs text-slate-400">No field-level changes recorded.</p>;
  }

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px]">Field</th>
            <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px]">Before</th>
            <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px]">After</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {keys.map((k) => (
            <tr key={k}>
              <td className="px-3 py-2 font-semibold text-slate-600 align-top whitespace-nowrap">{k}</td>
              <td className="px-3 py-2 text-red-600 align-top break-all">{String(oldValues?.[k] ?? '—')}</td>
              <td className="px-3 py-2 text-emerald-700 align-top break-all">{String(newValues?.[k] ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [expanded, setExpanded] = useState(null);

  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [meta, setMeta] = useState({ actions: [], entity_types: [] });

  useEffect(() => {
    auditLogAPI.meta().then(setMeta).catch(() => {});
  }, []);

  function load() {
    setLoading(true);
    auditLogAPI.list({ page, limit: 25, search, action, entity_type: entityType, from, to })
      .then((res) => {
        setLogs(res.logs || []);
        setPagination({
          page: res.page || 1,
          total: res.total || 0,
          total_pages: res.total_pages || 1,
          has_next: !!res.has_next,
          has_previous: !!res.has_previous,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(load, [page, action, entityType, from, to]);

  let searchTimer;
  function handleSearchChange(e) {
    const v = e.target.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { setSearch(v); setPage(1); }, 350);
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Audit Trail</h1>
        <p className="text-sm text-slate-400 mt-0.5">{pagination.total.toLocaleString()} recorded action(s)</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <input type="text" placeholder="Search actor, entity, action…" onChange={handleSearchChange}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">All Actions</option>
            {meta.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">All Entities</option>
            {meta.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex gap-2">
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-1">
            <p className="text-slate-500 font-medium text-sm">No matching activity found</p>
            <p className="text-xs text-slate-400">Try adjusting the filters above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Date &amp; Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Actor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">IP Address</th>
                  <th className="px-4 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <Fragment key={log.id}>
                    <tr className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap font-mono">{log.created_at}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800 text-sm">{log.actor_name || 'System'}</p>
                        {log.actor_role && <p className="text-[11px] text-slate-400">{log.actor_role}</p>}
                      </td>
                      <td className="px-4 py-3">{actionBadge(log.action)}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        <span className="font-semibold">{log.entity_type}</span>
                        {log.entity_id && <span className="text-slate-400"> #{log.entity_id}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{log.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                          className={`h-4 w-4 text-slate-400 transition-transform ${expanded === log.id ? 'rotate-180' : ''}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                        </svg>
                      </td>
                    </tr>
                    {expanded === log.id && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50 px-4 py-4">
                          <ValueDiff oldValues={log.old_values} newValues={log.new_values} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-400">
              Page {pagination.page} of {pagination.total_pages} · {pagination.total.toLocaleString()} total
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!pagination.has_previous}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Prev
              </button>
              <button onClick={() => setPage((p) => p + 1)} disabled={!pagination.has_next}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
